import React, { useState, useEffect } from 'react';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  SaveOutlined,
  SyncOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Card,
  Switch,
  Space,
  Tag,
  Divider,
  Button,
  message,
  Alert,
  Tooltip,
  Row,
  Col,
  Tabs,
} from 'antd';

import SuperAdminAPI from '../../../services/SuperAdminAPI';

const TenantFeaturesDrawer = ({ visible, onClose, tenant }) => {
  const [features, setFeatures] = useState({});
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleFeatures, setRoleFeatures] = useState({});
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [activeTab, setActiveTab] = useState('tenant');

  const categoryColors = {
    AI: '#722ed1',
    ANALYSIS: '#13c2c2',
    DOCUMENTS: '#52c41a',
    CASES: '#faad14',
    COLLABORATION: '#1890ff',
    INTEGRATIONS: '#fa8c16',
  };

  const featureCatalog = [
    {
      key: 'WHITEBOARD',
      icon: '🗺️',
      name: 'Whiteboards',
      description: 'Collaborative legal strategy whiteboards',
      category: 'COLLABORATION',
    },
    {
      key: 'EXCEL_INTELLIGENCE',
      icon: '📊',
      name: 'Excel Intelligence',
      description: 'AI-powered Excel data analysis',
      category: 'ANALYSIS',
    },
    {
      key: 'CASE_MANAGEMENT',
      icon: '⚖️',
      name: 'Case Management',
      description: 'Case tracking and timeline automation',
      category: 'CASES',
    },
    {
      key: 'DOCUMENT_COMPARISON',
      icon: '📄',
      name: 'Document Comparison',
      description: 'Side-by-side document comparison',
      category: 'DOCUMENTS',
    },
    {
      key: 'AI_ASSISTANT',
      icon: '🧠',
      name: 'AI Assistant',
      description: 'GPT-5 powered legal assistant',
      category: 'AI',
    },
    {
      key: 'CONTRACT_ANALYSIS',
      icon: '✍️',
      name: 'Contract Analysis',
      description: 'Deep contract analysis and clause extraction',
      category: 'ANALYSIS',
    },
    {
      key: 'FASTLEARN',
      icon: '🎓',
      name: 'FastLearn AI',
      description: 'AI-powered legal learning system',
      category: 'AI',
    },
    {
      key: 'EMAIL_MANAGEMENT',
      icon: '📨',
      name: 'Email Management',
      description: 'Integrated email tracking',
      category: 'INTEGRATIONS',
    },
    {
      key: 'VOICE_ASSISTANT',
      icon: '🎤',
      name: 'Voice Assistant',
      description: 'Voice-activated AI assistant',
      category: 'AI',
    },
    {
      key: 'MINDMAPPING',
      icon: '🧠',
      name: 'Mind Mapping',
      description: 'Visual case and strategy mapping',
      category: 'COLLABORATION',
    },
    {
      key: 'GAME_THEORY',
      icon: '🎯',
      name: 'Game Theory Analysis',
      description: 'Strategic analysis and simulations',
      category: 'ANALYSIS',
    },
    {
      key: 'CLIENT_MANAGEMENT',
      icon: '👤',
      name: 'Client Management',
      description: 'CRM and client tracking',
      category: 'CASES',
    },
  ];

  useEffect(() => {
    if (visible && tenant) {
      fetchTenantFeatures();
      fetchRoles();
    }
  }, [visible, tenant]);

  useEffect(() => {
    if (selectedRoleId) {
      fetchRoleFeatures();
    }
  }, [selectedRoleId]);

  const fetchTenantFeatures = async () => {
    if (!tenant?._id) return;
    setLoading(true);
    try {
      const response = await SuperAdminAPI.getTenantFeatures(tenant._id);
      setFeatures(response || {});
    } catch (error) {
      console.error('Error fetching features:', error);
      message.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    if (!tenant?._id) return;
    try {
      const response = await SuperAdminAPI.listRoles(tenant._id);
      const rolesData = response?.items || response?.roles || response || [];
      setRoles(rolesData);
      if (rolesData.length > 0) {
        setSelectedRoleId(rolesData[0]._id);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchRoleFeatures = async () => {
    if (!tenant?._id || !selectedRoleId) return;
    try {
      const response = await SuperAdminAPI.getTenantRoleFeatures(tenant._id, selectedRoleId);
      setRoleFeatures(response || {});
    } catch (error) {
      console.error('Error fetching role features:', error);
    }
  };

  const handleTenantToggle = async (featureCode, enabled) => {
    if (!tenant?._id) return;
    setUpdating(featureCode);
    try {
      await SuperAdminAPI.updateTenantFeatures(tenant._id, {
        [featureCode]: enabled,
      });
      setFeatures(prev => ({
        ...prev,
        [featureCode]: { ...prev[featureCode], enabled, source: 'tenant' },
      }));
      message.success(
        <span>
          Feature <strong>{featureCode}</strong> is now{' '}
          <strong style={{ color: enabled ? '#52c41a' : '#ff4d4f' }}>
            {enabled ? 'ON' : 'OFF'}
          </strong>{' '}
          for {tenant.name}
        </span>
      );
    } catch (error) {
      console.error('Error toggling feature:', error);
      message.error('Failed to update feature');
    } finally {
      setUpdating(null);
    }
  };

  const handleRoleToggle = async (featureCode, enabled) => {
    if (!tenant?._id || !selectedRoleId) return;
    setUpdating(featureCode);
    try {
      await SuperAdminAPI.updateTenantRoleFeatures(tenant._id, selectedRoleId, {
        featureCode,
        enabled,
      });
      setRoleFeatures(prev => ({
        ...prev,
        [featureCode]: { ...prev[featureCode], enabled, source: 'role' },
      }));
      const roleName = roles.find(r => r._id === selectedRoleId)?.name || 'this role';
      message.success(
        <span>
          Feature <strong>{featureCode}</strong> is now{' '}
          <strong style={{ color: enabled ? '#52c41a' : '#ff4d4f' }}>
            {enabled ? 'ON' : 'OFF'}
          </strong>{' '}
          for {roleName}
        </span>
      );
    } catch (error) {
      console.error('Error toggling feature:', error);
      message.error('Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  // Group features by category
  const groupByCategory = featuresData => {
    const grouped = {};
    featureCatalog.forEach(feature => {
      if (!grouped[feature.category]) {
        grouped[feature.category] = [];
      }
      const featureData = featuresData[feature.key] || {};
      grouped[feature.category].push({
        ...feature,
        enabled: featureData.enabled || false,
        source: featureData.source || 'global',
      });
    });
    return grouped;
  };

  if (!tenant) return null;

  const groupedTenantFeatures = groupByCategory(features);
  const groupedRoleFeatures = groupByCategory(roleFeatures);
  const selectedRole = roles.find(r => r._id === selectedRoleId);

  return (
    <Drawer
      title={`🧰 Feature Management - ${tenant.name}`}
      placement="right"
      width={700}
      open={visible}
      onClose={onClose}
    >
      <Alert
        message={`Current Plan: ${tenant.plan || 'Basic'}`}
        description="Manage feature access at tenant and role level"
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'tenant',
            label: (
              <span>
                <SettingOutlined /> Tenant Features
              </span>
            ),
            children: (
              <>
                {Object.entries(groupedTenantFeatures).map(([category, categoryFeatures]) => (
                  <div key={category}>
                    <Divider orientation="left">
                      <Space>
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: categoryColors[category] || '#999',
                            display: 'inline-block',
                          }}
                        />
                        <strong>{category}</strong>
                      </Space>
                    </Divider>

                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: '100%', marginBottom: 16 }}
                    >
                      {categoryFeatures.map(feature => (
                        <Card
                          key={feature.key}
                          size="small"
                          style={{
                            backgroundColor: feature.enabled ? '#f6ffed' : '#fff',
                            borderColor: feature.enabled ? '#b7eb8f' : '#d9d9d9',
                          }}
                        >
                          <Row gutter={[16, 16]} align="middle">
                            <Col flex="auto">
                              <Space direction="vertical" size={0}>
                                <Space size="small">
                                  <span>{feature.icon}</span>
                                  <strong style={{ color: '#262626' }}>{feature.name}</strong>
                                  <Tag
                                    color={
                                      feature.source === 'tenant'
                                        ? 'blue'
                                        : feature.source === 'plan'
                                          ? 'green'
                                          : 'default'
                                    }
                                    style={{ fontSize: 10 }}
                                  >
                                    {feature.source === 'tenant'
                                      ? 'CUSTOM'
                                      : feature.source === 'plan'
                                        ? 'PLAN'
                                        : 'DEFAULT'}
                                  </Tag>
                                  <Tag
                                    color={feature.enabled ? 'success' : 'error'}
                                    style={{ fontSize: 10, fontWeight: 'bold' }}
                                  >
                                    {feature.enabled ? 'ON' : 'OFF'}
                                  </Tag>
                                </Space>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                  {feature.description}
                                </div>
                              </Space>
                            </Col>
                            <Col>
                              <Switch
                                checked={feature.enabled}
                                onChange={checked => handleTenantToggle(feature.key, checked)}
                                loading={updating === feature.key}
                                size="small"
                                style={{
                                  backgroundColor: feature.enabled ? '#52c41a' : '#d9d9d9',
                                }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </Space>
                  </div>
                ))}
              </>
            ),
          },
          {
            key: 'roles',
            label: (
              <span>
                <TeamOutlined /> Role Permissions
              </span>
            ),
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Select Role:</div>
                    <select
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                      }}
                      value={selectedRoleId}
                      onChange={e => setSelectedRoleId(e.target.value)}
                    >
                      {roles.map(role => (
                        <option key={role._id} value={role._id}>
                          {role.name} {role.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedRole && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                        {selectedRole.description}
                      </div>
                    )}
                  </Space>
                </Card>

                {selectedRoleId &&
                  Object.entries(groupedRoleFeatures).map(([category, categoryFeatures]) => (
                    <div key={category}>
                      <Divider orientation="left">
                        <Space>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: categoryColors[category] || '#999',
                              display: 'inline-block',
                            }}
                          />
                          <strong>{category}</strong>
                        </Space>
                      </Divider>

                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: '100%', marginBottom: 16 }}
                      >
                        {categoryFeatures.map(feature => (
                          <Card
                            key={feature.key}
                            size="small"
                            style={{
                              backgroundColor: feature.enabled ? '#f6ffed' : '#fff',
                              borderColor: feature.enabled ? '#b7eb8f' : '#d9d9d9',
                            }}
                          >
                            <Row gutter={[16, 16]} align="middle">
                              <Col flex="auto">
                                <Space direction="vertical" size={0}>
                                  <Space size="small">
                                    <span>{feature.icon}</span>
                                    <strong style={{ fontSize: 13, color: '#262626' }}>
                                      {feature.name}
                                    </strong>
                                    <Tag
                                      color={
                                        feature.source === 'role'
                                          ? 'blue'
                                          : feature.source === 'tenant'
                                            ? 'cyan'
                                            : feature.source === 'plan'
                                              ? 'green'
                                              : 'default'
                                      }
                                      style={{ fontSize: 10 }}
                                    >
                                      {feature.source === 'role'
                                        ? 'ROLE'
                                        : feature.source === 'tenant'
                                          ? 'TENANT'
                                          : feature.source === 'plan'
                                            ? 'PLAN'
                                            : 'DEFAULT'}
                                    </Tag>
                                    <Tag
                                      color={feature.enabled ? 'success' : 'error'}
                                      style={{ fontSize: 10, fontWeight: 'bold' }}
                                    >
                                      {feature.enabled ? 'ON' : 'OFF'}
                                    </Tag>
                                  </Space>
                                  <div style={{ fontSize: 11, color: '#666' }}>
                                    {feature.description}
                                  </div>
                                </Space>
                              </Col>
                              <Col>
                                <Switch
                                  checked={feature.enabled}
                                  onChange={checked => handleRoleToggle(feature.key, checked)}
                                  loading={updating === feature.key}
                                  size="small"
                                  style={{
                                    backgroundColor: feature.enabled ? '#52c41a' : '#d9d9d9',
                                  }}
                                />
                              </Col>
                            </Row>
                          </Card>
                        ))}
                      </Space>
                    </div>
                  ))}
              </>
            ),
          },
        ]}
      />

      <Divider />
      <Card size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                {
                  Object.values(activeTab === 'tenant' ? features : roleFeatures).filter(
                    f => f.enabled
                  ).length
                }
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>Enabled</div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#999' }}>
                {featureCatalog.length}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>Total Features</div>
            </div>
          </Col>
        </Row>
      </Card>
    </Drawer>
  );
};

export default TenantFeaturesDrawer;
